import { useMemo, useState, useEffect } from 'react';
import Validation from '~/util/Validation';
import { Lfa_Calendar_Type } from '~/graphql/types';
import { TEXT } from '../../constants';
import type { UpdateSettingsState } from '../../reducers/settingsReducer';
import type { ServicesV2State } from '../../reducers/servicesV2Reducer';
import resolveServicesForOffice from '~/components/page/Settings/LFASettings/servicesV2/utils/resolveServicesForOffice';
import hasValue from '~/util/hasValue';

type ServiceNameLookup = Map<string, string>;
type OfficeNameLookup = Map<string, string>;

type Args = {
  updateSettings: UpdateSettingsState;
  servicesV2: ServicesV2State;
  serviceNames?: ServiceNameLookup;
  officeNames?: OfficeNameLookup;
};

type ValidationErrors = {
  leadscore: Array<string>;
  locations: Array<string>;
  servicesV2Locations: Array<string>;
  servicesV2Calendars: Array<string>;
  servicesV2Credentials: Array<string>;
  phoneNumber: Array<string>;
  flynck: Array<string>;
};

export type TouchedFields = {
  leadscore: boolean;
  flynck: boolean;
  locations: boolean;
  phoneNumber: boolean;
};

type ReturnType = {
  errors: ValidationErrors;
  hasErrors: boolean;
  setFieldTouched: (field: keyof TouchedFields) => void;
  clearAllErrors: () => void;
};

const useSettingsValidation = ({
  updateSettings,
  servicesV2,
  serviceNames,
  officeNames,
}: Args): ReturnType => {
  const [errors, setErrors] = useState<ValidationErrors>({
    leadscore: [],
    locations: [],
    servicesV2Locations: [],
    servicesV2Calendars: [],
    servicesV2Credentials: [],
    phoneNumber: [],
    flynck: [],
  });

  const [touchedFields, setTouchedFields] = useState<TouchedFields>({
    leadscore: false,
    flynck: false,
    locations: false,
    phoneNumber: false,
  });

  const validationErrors = useMemo((): ValidationErrors => {
    const leadscoreErrors: Array<string> = [];
    const locationsErrors: Array<string> = [];
    const servicesV2LocationsErrors: Array<string> = [];
    const servicesV2CalendarsErrors: Array<string> = [];
    const servicesV2CredentialsErrors: Array<string> = [];
    const phoneNumberErrors: Array<string> = [];
    const flynckErrors: Array<string> = [];

    if (servicesV2) {
      const svcName = (id: string) => serviceNames?.get(id) ?? id;
      const oName = (id: string) => officeNames?.get(id) ?? id;

      // Account-level: every service must have at least one location
      for (const s of servicesV2.accountServices) {
        if (s.locations.length === 0) {
          servicesV2LocationsErrors.push(
            `Account — "${svcName(s.id)}": minimaal één locatie vereist`,
          );
        }
      }

      // Collect all office IDs: from changesets + from office lookup
      const allOfficeIds = new Set<string>();
      for (const entry of servicesV2.officeChangesets) {
        allOfficeIds.add(entry.officeId);
      }
      if (officeNames) {
        for (const oid of officeNames.keys()) {
          allOfficeIds.add(oid);
        }
      }

      for (const officeId of allOfficeIds) {
        const resolved = resolveServicesForOffice({
          accountServices: servicesV2.accountServices,
          officeChangesets: servicesV2.officeChangesets,
          officeId,
        });
        for (const s of resolved) {
          if (s.locations.length === 0) {
            servicesV2LocationsErrors.push(
              `${oName(officeId)} — "${svcName(s.id)}": minimaal één locatie vereist`,
            );
          }
          if (s.calendars.length === 0) {
            servicesV2CalendarsErrors.push(
              `${oName(officeId)} — "${svcName(s.id)}": minimaal één agenda vereist`,
            );
          }
        }
      }

      const calendarWithBadLink = servicesV2.calendars.some(c => {
        if (
          c.type !== Lfa_Calendar_Type.Google &&
          c.type !== Lfa_Calendar_Type.Unknown
        )
          return false;
        const link = c.link?.trim();
        if (!link) return true;
        return !link.startsWith('https://') || link.length <= 'https://'.length;
      });
      if (calendarWithBadLink) {
        servicesV2CalendarsErrors.push(TEXT.servicesV2CalendarLinkRequired);
      }

      // Credentials: require name, username, and password
      for (const cred of servicesV2.credentials) {
        const missing: Array<string> = [];
        if (!cred.name.trim()) missing.push('naam');
        if (!cred.username.trim()) missing.push('gebruikersnaam');
        if (!cred.password.trim()) missing.push('wachtwoord');
        if (missing.length > 0) {
          servicesV2CredentialsErrors.push(
            `"${cred.name || 'Nieuwe inloggegevens'}": ${missing.join(', ')} is verplicht`,
          );
        }
      }

      // Orphan credential references: calendar refers to a deleted credential
      const credentialIds = new Set(servicesV2.credentials.map(c => c.id));
      for (const cal of servicesV2.calendars) {
        if (
          cal.credentials?.__typename === 'LFA_Calendar_Credentials_Ref' &&
          !credentialIds.has(cal.credentials.id)
        ) {
          servicesV2CredentialsErrors.push(
            `Agenda "${cal.name || 'Nieuwe agenda'}": verwijst naar verwijderde inloggegevens`,
          );
        }
      }
    }

    // Leadscore validation
    if (touchedFields.leadscore) {
      const leadscore = updateSettings.rules.minLeadscore;
      const numberValue = Number(leadscore);

      if (!hasValue(leadscore) || !Validation.Number.isNumber(numberValue)) {
        leadscoreErrors.push(TEXT.invalidLeadscoreNumber);
      }
      if (numberValue <= 0 || numberValue > 100) {
        leadscoreErrors.push(TEXT.invalidMinLeadscore);
      }
    }

    // Flynck validation
    if (touchedFields.flynck) {
      const flynck = updateSettings.flynck;
      const missingField =
        !hasValue(flynck?.importSetupID) || !hasValue(flynck?.contextID);

      if (missingField) {
        flynckErrors.push(TEXT.flynkSettingsErrors);
      }
    }

    // Locations validation
    if (touchedFields.locations) {
      const services = updateSettings.services;
      const locationsSelectedForEachService = services.every(service =>
        service.locations.some(location => location.enabled),
      );

      if (!locationsSelectedForEachService) {
        locationsErrors.push(TEXT.noLocationsSelectedError);
      }
    }

    // Phone number validation
    if (touchedFields.phoneNumber) {
      const callbackPhoneNumber = updateSettings.callbackPhoneNumber;

      if (!hasValue(callbackPhoneNumber)) {
        phoneNumberErrors.push(TEXT.invalidPhoneNumber);
      }
    }

    return {
      leadscore: leadscoreErrors,
      locations: locationsErrors,
      servicesV2Locations: servicesV2LocationsErrors,
      servicesV2Calendars: servicesV2CalendarsErrors,
      servicesV2Credentials: servicesV2CredentialsErrors,
      phoneNumber: phoneNumberErrors,
      flynck: flynckErrors,
    };
  }, [updateSettings, touchedFields, servicesV2, serviceNames, officeNames]);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  const hasErrors = useMemo(
    () => Object.values(errors).some(errorArray => errorArray.length > 0),
    [errors],
  );

  const setFieldTouched = (field: keyof TouchedFields) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true,
    }));
  };

  const clearAllErrors = () => {
    setErrors({
      leadscore: [],
      locations: [],
      servicesV2Locations: [],
      servicesV2Calendars: [],
      servicesV2Credentials: [],
      phoneNumber: [],
      flynck: [],
    });

    setTouchedFields({
      leadscore: false,
      flynck: false,
      locations: false,
      phoneNumber: false,
    });
  };

  return { errors, hasErrors, setFieldTouched, clearAllErrors };
};

export default useSettingsValidation;
